import React from "react";
import { v4 as uuidv4 } from "uuid";
import FormField from "./FormField";
import { getVisibleFields } from "utilities/schemaEval";
import { Plus, Trash2, UserPlus } from "lucide-react";

/**
 * Renders a repeatable array section (DBAs or Owners).
 * Handles add/remove items, conditional fields per item, signer sub-section for owners.
 */
const RepeatableSectionRenderer = ({ stepSchema, data, onChange, fullData }) => {
  const { arrayField, maxItems, fields, itemLabel, addButtonLabel, signerSection } = stepSchema;
  const items = data?.[arrayField] || [];

  // For owners: separate owners vs signers
  const isOwnerSection = arrayField === "owners";
  const ownerItems = isOwnerSection ? items.filter((i) => i.ownerType !== "signer") : items;
  const signerItems = isOwnerSection ? items.filter((i) => i.ownerType === "signer") : [];
  const hasNonUsOwner = isOwnerSection && ownerItems.some((o) => o.citizenshipType === "non-us");

  // Calculate ownership total for owners
  const ownershipTotal = isOwnerSection
    ? ownerItems.reduce((sum, o) => {
        const pct = Number(o.ownershipPercentage);
        return sum + (isNaN(pct) ? 0 : pct);
      }, 0)
    : null;

  const handleItemChange = (index, fieldName, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [fieldName]: value };
    onChange(arrayField, null, updated);
  };

  const addItem = (ownerType = "owner") => {
    const idField = arrayField === "dbas" ? "dbaId" : "ownerId";
    const newItem = { [idField]: uuidv4() };

    // Initialize all fields to empty
    for (const field of fields) {
      newItem[field.name] = "";
    }

    if (arrayField === "dbas") {
      newItem.isActive = true;
    }
    if (arrayField === "owners") {
      newItem.ownerType = ownerType;
      newItem.citizenshipType = ownerType === "signer" ? "us" : "";
      if (ownerType === "signer") {
        newItem.ownershipPercentage = "0";
      }
    }

    onChange(arrayField, null, [...items, newItem]);
  };

  const removeItem = (index) => {
    if (items.length <= 1 && !isOwnerSection) return;
    // For owners, don't remove the last non-signer owner
    if (isOwnerSection) {
      const item = items[index];
      if (item.ownerType !== "signer" && ownerItems.length <= 1) return;
    }
    const updated = items.filter((_, i) => i !== index);
    onChange(arrayField, null, updated);
  };

  const renderItemFields = (item, globalIndex) => {
    let visibleFields = getVisibleFields(fields, item, fullData);

    // Signers are always US — hide the citizenship toggle
    if (item.ownerType === "signer") {
      visibleFields = visibleFields.filter((f) => f.name !== "citizenshipType");
    }

    // Build grid blocks (same logic as StepRenderer)
    const blocks = [];
    let currentBlock = null;

    for (const field of visibleFields) {
      const cols = field.gridCols || 2;
      if (!currentBlock || currentBlock.cols !== cols) {
        currentBlock = { cols, fields: [] };
        blocks.push(currentBlock);
      }
      currentBlock.fields.push(field);
    }

    return blocks.map((block, bi) => (
      <div
        key={bi}
        className={
          block.cols === 1
            ? "grid grid-cols-1 gap-4"
            : "grid grid-cols-1 md:grid-cols-2 gap-4"
        }
      >
        {block.fields.map((field) => (
          <FormField
            key={field.name}
            label={field.label}
            name={field.name}
            value={item[field.name] || ""}
            onChange={(name, value) => handleItemChange(globalIndex, name, value)}
            type={field.type || "text"}
            required={field.required}
            placeholder={field.placeholder}
            options={field.options}
            rows={field.rows}
            tooltip={field.tooltip}
            otherField={field.otherField}
            otherValue={item[field.otherField] || ""}
            onOtherChange={(otherName, otherVal) =>
              handleItemChange(globalIndex, otherName, otherVal)
            }
          />
        ))}
      </div>
    ));
  };

  const canAddMore = !maxItems || ownerItems.length < maxItems;
  const canAddSigner =
    isOwnerSection &&
    hasNonUsOwner &&
    signerSection &&
    signerItems.length < (signerSection.maxSigners || 1);

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-normal text-foreground mb-2">
          {stepSchema.title}<span className="text-brand">.</span>
        </h2>
        <p className="text-muted-foreground font-light">
          {stepSchema.description}
        </p>
      </div>

      {/* Ownership running total */}
      {isOwnerSection && ownershipTotal !== null && (
        <div
          className={`text-sm font-medium text-center px-4 py-2 rounded-lg border ${
            ownershipTotal === 100
              ? "bg-success/10 border-success/30 text-success"
              : "bg-warning/10 border-warning/30 text-warning"
          }`}
        >
          Ownership Total: {ownershipTotal}%{" "}
          {ownershipTotal !== 100 && "(must equal 100%)"}
        </div>
      )}

      {/* Owner items */}
      {ownerItems.map((item, i) => {
        const globalIndex = items.indexOf(item);
        const canRemove = isOwnerSection ? ownerItems.length > 1 : items.length > 1;

        return (
          <div
            key={item.ownerId || item.dbaId || i}
            className="bg-muted/20 border border-border rounded-lg p-6 space-y-4"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-normal text-foreground">
                {itemLabel} {ownerItems.length > 1 ? i + 1 : ""}
              </h3>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => removeItem(globalIndex)}
                  className="text-red-400 hover:text-red-300 transition-colors p-1"
                  title={`Remove ${itemLabel}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {renderItemFields(item, globalIndex)}
          </div>
        );
      })}

      {/* Add button */}
      {canAddMore && (
        <button
          type="button"
          onClick={() => addItem("owner")}
          className="flex items-center space-x-2 text-brand hover:text-brand-400 transition-colors text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          <span>{addButtonLabel || `Add ${itemLabel}`}</span>
        </button>
      )}

      {/* Signer sub-section (owners only, when non-US owners exist) */}
      {isOwnerSection && hasNonUsOwner && signerSection && (
        <div className="mt-8 border-t border-border pt-6">
          <h3 className="text-lg font-normal text-foreground mb-2">
            {signerSection.label}
          </h3>
          <p className="text-sm text-muted-foreground font-light mb-4">
            {signerSection.description}
          </p>

          {signerItems.map((signer) => {
            const globalIndex = items.indexOf(signer);
            return (
              <div
                key={signer.ownerId || globalIndex}
                className="bg-blue-900/10 border border-blue-500/30 rounded-lg p-6 space-y-4"
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-normal text-blue-400">
                    US Authorized Signer
                  </h3>
                  <button
                    type="button"
                    onClick={() => removeItem(globalIndex)}
                    className="text-red-400 hover:text-red-300 transition-colors p-1"
                    title="Remove Signer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                {renderItemFields(signer, globalIndex)}
              </div>
            );
          })}

          {canAddSigner && (
            <button
              type="button"
              onClick={() => addItem("signer")}
              className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium mt-3"
            >
              <UserPlus className="w-4 h-4" />
              <span>{signerSection.addButtonLabel || "Add US Signer"}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default RepeatableSectionRenderer;
